/**
 * The control panel follows Giraffe's theme, and it needs **no SDK support** to
 * do it.
 *
 * ## The one bit that crosses the boundary
 *
 * Giraffe's theme classes each declare `color-scheme` (`.mondrian.theme-minimalist`
 * -> light, `.mondrian.theme-minimalist-dark` -> dark). That class sits on the
 * host's `<html>`, the `<iframe>` element inherits the computed value, and CSS
 * Color Adjust propagates an embedder's used `color-scheme` into the embedded
 * document as its PREFERRED colour scheme. So inside the panel,
 * `prefers-color-scheme` reports **Giraffe's theme, not the user's OS**.
 *
 * ⚠️ Provenance: verified live 2026-09-04 by `non-core-apps/multifamily`, with
 * the OS on dark throughout — host on `theme-minimalist` -> the frame reads
 * light; `theme-minimalist-dark` -> dark; `theme-clown` -> light. Verified
 * against that build, not a contract. Measured in the same pass and just as
 * load-bearing: every `--mondrian-*` custom property reads **empty** inside the
 * frame, a custom property set directly on the `<iframe>` element does not
 * cross, and the host's Inter does not either. Even `color-scheme` itself
 * computes `normal` in there — the propagation is visible only through
 * `matchMedia`.
 *
 * Two consequences, and the whole design rests on them:
 *
 * - the VALUES must be vendored (`mondrian-tokens.css`, `yarn vendor:mondrian`)
 *   because they can never be read from the host;
 * - we learn the host's **lightness**, never its **palette identity**. A
 *   `theme-clown` host reports `light`, so we render our own light palette
 *   inside it. Matching the host's actual palette would need the SDK to name
 *   its theme, and no such key exists anywhere in `@gi-nx/*`.
 *
 * ## Why there is no override, and no stored preference
 *
 * With the host's theme readable for free, a Light/Dark/System control could
 * only ever make the panel disagree with the chrome it sits in.
 *
 * ## Why this listens, where multifamily applies once
 *
 * That app paints the theme from an inline `<script>` in its `index.html`,
 * before first paint. This page cannot: its CSP is `script-src 'self'` with no
 * `'unsafe-inline'`, and relaxing that to save one frame of flash would be a
 * poor trade on the page whose whole posture is `default-src 'none'`. So the
 * class is applied when the bundle runs, and a `change` listener carries it
 * afterwards — which costs a frame at boot and buys a panel that re-themes
 * live when the user switches Giraffe's theme with the panel open.
 *
 * The pre-paint frame is not left white-on-dark: both page templates
 * (`controlWorker.ts` and `scripts/dev-server.mjs`) carry `color-scheme: light
 * dark` and a `light-dark()` body background, so the first paint lands on
 * roughly the right ground and this module refines it.
 */

/** Production's selectors are `.mondrian.theme-x` — two classes. Both required. */
const MONDRIAN_CLASS = "mondrian";

/**
 * Every theme class production ships. We render app blocks for the minimalist
 * pair only; the rest are listed so `applyTheme` can clear one it did not set —
 * which matters because these are PRODUCTION's class names, not ours, and
 * nothing stops the vendored file from growing another.
 */
export const THEME_CLASSES = [
  "theme-light",
  "theme-dark",
  "theme-dark-blue",
  "theme-minimalist",
  "theme-minimalist-dark",
  "theme-clown",
] as const;

export type ThemeClass = (typeof THEME_CLASSES)[number];

const OUR_LIGHT: ThemeClass = "theme-minimalist";
const OUR_DARK: ThemeClass = "theme-minimalist-dark";

/**
 * `prefersDark` is the host's theme when embedded, and the OS only when the
 * page is opened at its own origin — which for this app is a broken state
 * anyway (no host means no token and no project), but it still renders
 * something legible rather than unstyled.
 *
 * Kept pure so it is testable with no DOM.
 */
export function resolveThemeClass(prefersDark: boolean): ThemeClass {
  return prefersDark ? OUR_DARK : OUR_LIGHT;
}

/**
 * Put the palette on `<html>` and keep it there.
 *
 * Returns nothing to unsubscribe with on purpose: the panel lives exactly as
 * long as the document does.
 */
export function followHostTheme(root: HTMLElement, media: MediaQueryList): void {
  const apply = (prefersDark: boolean): void => {
    root.classList.add(MONDRIAN_CLASS);
    root.classList.remove(...THEME_CLASSES);
    root.classList.add(resolveThemeClass(prefersDark));
  };
  apply(media.matches);
  media.addEventListener("change", (event) => {
    apply(event.matches);
  });
}
